import { useEffect, useMemo, useState } from 'react';
import { addMonths, format } from 'date-fns';
import { Check, ChevronRight, Crown, Loader2, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';

type UseCase = 'couples' | 'freelance' | 'personal';

type RecurringDraft = {
  name: string;
  amount: string;
};

const USE_CASE_OPTIONS: Array<{ value: UseCase; title: string; subtitle: string }> = [
  {
    value: 'couples',
    title: 'Coppie',
    subtitle: 'Traccia spese condivise e obiettivi comuni.',
  },
  {
    value: 'freelance',
    title: 'Freelance',
    subtitle: 'Controlla cashflow e costi ricorrenti di lavoro.',
  },
  {
    value: 'personal',
    title: 'Personale',
    subtitle: 'Gestisci budget personale in modo semplice.',
  },
];

const AUTOCOMPLETE_SUGGESTIONS = [
  'Netflix',
  'Spotify',
  'Amazon Prime',
  'Disney+',
  'Apple Music',
  'YouTube Premium',
  'Google One',
  'ChatGPT Plus',
];

function toCategory(serviceName: string): string {
  const lower = serviceName.toLowerCase();
  if (
    lower.includes('netflix') ||
    lower.includes('spotify') ||
    lower.includes('prime') ||
    lower.includes('disney') ||
    lower.includes('youtube') ||
    lower.includes('music')
  ) {
    return 'entertainment';
  }
  if (lower.includes('chatgpt') || lower.includes('google one')) {
    return 'utilities';
  }
  return 'other';
}

function normalizeRecurring(drafts: RecurringDraft[]) {
  return drafts
    .map((d) => ({
      name: d.name.trim(),
      amount: Number(d.amount),
    }))
    .filter((d) => d.name.length > 0 && Number.isFinite(d.amount) && d.amount > 0);
}

function estimateWasteYearly(drafts: RecurringDraft[]): number {
  const recurring = normalizeRecurring(drafts);
  return recurring.reduce((sum, item) => {
    const lower = item.name.toLowerCase();
    const factor =
      lower.includes('netflix') ||
      lower.includes('spotify') ||
      lower.includes('prime') ||
      lower.includes('disney')
        ? 0.35
        : lower.includes('chatgpt') || lower.includes('google one')
          ? 0.2
          : 0.15;
    return sum + item.amount * 12 * factor;
  }, 0);
}

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth();

  const [step, setStep] = useState(1);
  const [useCase, setUseCase] = useState<UseCase | null>(null);
  const [recurringDrafts, setRecurringDrafts] = useState<RecurringDraft[]>([
    { name: 'Netflix', amount: '15.99' },
    { name: 'Spotify', amount: '10.99' },
  ]);
  const [saving, setSaving] = useState(false);

  const recurringNormalized = useMemo(() => normalizeRecurring(recurringDrafts), [recurringDrafts]);
  const wasteYearly = useMemo(() => estimateWasteYearly(recurringDrafts), [recurringDrafts]);

  const onboardingCompleted = (profile as { onboarding_completed?: boolean | null } | null)?.onboarding_completed;

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    if (onboardingCompleted === true) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, onboardingCompleted, navigate]);

  if (!user || onboardingCompleted === true) {
    return null;
  }

  const addRecurringRow = () => {
    setRecurringDrafts((prev) => [...prev, { name: '', amount: '' }]);
  };

  const updateRecurringRow = (idx: number, patch: Partial<RecurringDraft>) => {
    setRecurringDrafts((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };

  const removeRecurringRow = (idx: number) => {
    setRecurringDrafts((prev) => prev.filter((_, i) => i !== idx));
  };

  const seedInitialDataIfNeeded = async () => {
    const { count, error: countError } = await supabase
      .from('expenses')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', user.id);

    if (countError) throw countError;
    if ((count ?? 0) > 0) return;

    const today = format(new Date(), 'yyyy-MM-dd');
    const nextDue = format(addMonths(new Date(), 1), 'yyyy-MM-dd');

    const templates = recurringNormalized.map((item) => ({
      user_id: user.id,
      description: item.name,
      amount: Number(item.amount.toFixed(2)),
      category: toCategory(item.name),
      recurring: true,
      recurring_type: 'monthly',
      next_due: nextDue,
      frequency_days: null,
      is_template: true,
      template_id: null,
      date: today,
    }));

    const loggedNow = recurringNormalized.map((item) => ({
      user_id: user.id,
      description: `${item.name} (Mese corrente)`,
      amount: Number(item.amount.toFixed(2)),
      category: toCategory(item.name),
      recurring: false,
      recurring_type: 'none',
      next_due: null,
      frequency_days: null,
      is_template: false,
      template_id: null,
      date: today,
    }));

    const rowsToInsert = [...templates, ...loggedNow];
    if (rowsToInsert.length === 0) return;

    const { error: insertError } = await supabase.from('expenses').insert(rowsToInsert);
    if (insertError) throw insertError;
  };

  const finishOnboarding = async () => {
    if (!useCase) {
      toast.error('Seleziona prima come usi BudgetAI');
      setStep(1);
      return;
    }

    setSaving(true);
    try {
      await seedInitialDataIfNeeded();

      const { error: profileError } = await supabase
        .from('users')
        .update({ onboarding_completed: true, onboarding_use_case: useCase })
        .eq('id', user.id);

      if (profileError) throw profileError;

      await refreshProfile();
      toast.success('Dashboard pronta: onboarding completato');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Errore durante onboarding');
    } finally {
      setSaving(false);
    }
  };

  const canGoStep2 = useCase !== null;
  const canGoStep3 = recurringNormalized.length > 0;

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-8 sm:px-6 lg:px-10">
      <div className="mx-auto w-full max-w-4xl animate-fade-in">
        <div className="mb-6 flex items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-brand-300">Welcome Flow</p>
            <h1 className="mt-1 text-2xl font-bold text-white sm:text-3xl">Configuriamo BudgetAI in meno di 3 minuti</h1>
          </div>
          <div className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-slate-300">
            Step {step}/4
          </div>
        </div>

        <div className="mb-6 h-2 overflow-hidden rounded-full bg-slate-800">
          <div
            className="h-full bg-gradient-to-r from-brand-500 to-cyan-400 transition-all duration-300"
            style={{ width: `${(step / 4) * 100}%` }}
          />
        </div>

        <div className="card p-5 sm:p-7">
          {step === 1 && (
            <div className="animate-slide-up">
              <h2 className="text-xl font-semibold text-white">Come usi BudgetAI?</h2>
              <p className="mt-1 text-sm text-slate-400">Adattiamo dashboard e suggerimenti al tuo contesto.</p>

              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                {USE_CASE_OPTIONS.map((option) => {
                  const active = useCase === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setUseCase(option.value)}
                      className={[
                        'rounded-xl border p-4 text-left transition-colors',
                        active
                          ? 'border-brand-500 bg-brand-500/10'
                          : 'border-slate-700 bg-slate-900/40 hover:border-slate-500',
                      ].join(' ')}
                    >
                      <p className="font-semibold text-white">{option.title}</p>
                      <p className="mt-1 text-xs text-slate-400">{option.subtitle}</p>
                      {active && (
                        <p className="mt-3 inline-flex items-center gap-1 text-xs text-brand-300">
                          <Check size={12} /> Selezionato
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="mt-6 flex justify-end">
                <Button onClick={() => setStep(2)} disabled={!canGoStep2} rightIcon={<ChevronRight size={15} />}>
                  Continua
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="animate-slide-up">
              <h2 className="text-xl font-semibold text-white">I tuoi ricorrenti principali</h2>
              <p className="mt-1 text-sm text-slate-400">
                Inserisci i servizi che paghi ogni mese. Suggerimenti rapidi: Netflix, Spotify.
              </p>

              <datalist id="recurring-suggestions">
                {AUTOCOMPLETE_SUGGESTIONS.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>

              <div className="mt-5 space-y-3">
                {recurringDrafts.map((row, idx) => (
                  <div key={`${idx}-${row.name}`} className="grid grid-cols-1 gap-3 rounded-xl border border-slate-800 bg-slate-900/50 p-3 sm:grid-cols-[1fr_140px_auto]">
                    <Input
                      list="recurring-suggestions"
                      placeholder="Es. Netflix"
                      value={row.name}
                      onChange={(e) => updateRecurringRow(idx, { name: e.target.value })}
                    />
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="€/mese"
                      value={row.amount}
                      onChange={(e) => updateRecurringRow(idx, { amount: e.target.value })}
                    />
                    <Button variant="ghost" onClick={() => removeRecurringRow(idx)}>
                      Rimuovi
                    </Button>
                  </div>
                ))}
              </div>

              <div className="mt-3">
                <Button variant="secondary" onClick={addRecurringRow}>
                  Aggiungi ricorrente
                </Button>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <Button variant="ghost" onClick={() => setStep(1)}>
                  Indietro
                </Button>
                <Button onClick={() => setStep(3)} disabled={!canGoStep3} rightIcon={<ChevronRight size={15} />}>
                  Avvia scanner
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="animate-slide-up">
              <h2 className="text-xl font-semibold text-white">Scanner completato</h2>
              <p className="mt-2 text-lg font-semibold text-amber-300">
                Hai potenzialmente €{wasteYearly.toFixed(0)} sprecati/anno
              </p>
              <p className="mt-1 text-sm text-slate-400">
                Stima basata sui ricorrenti inseriti e pattern di utilizzo tipici.
              </p>

              <div className="relative mt-5 overflow-hidden rounded-xl border border-slate-800 bg-slate-900/50 p-4">
                <div className="pointer-events-none blur-[3.5px] select-none">
                  <p className="text-sm text-slate-300">Dettagli ottimizzazione</p>
                  <ul className="mt-3 space-y-2 text-sm text-slate-400">
                    {recurringNormalized.map((item) => (
                      <li key={item.name}>• {item.name}: suggerita ottimizzazione €{(item.amount * 12 * 0.2).toFixed(0)}/anno</li>
                    ))}
                  </ul>
                </div>
                <div className="absolute inset-0 flex items-center justify-center bg-slate-950/40">
                  <Button onClick={() => navigate('/pricing')} leftIcon={<Crown size={15} />}>
                    1 settimana gratis, sblocca tutto
                  </Button>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <Button variant="ghost" onClick={() => setStep(2)}>
                  Indietro
                </Button>
                <Button onClick={() => setStep(4)} rightIcon={<ChevronRight size={15} />}>
                  Continua
                </Button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="animate-slide-up">
              <h2 className="text-xl font-semibold text-white">Dashboard pronta</h2>
              <p className="mt-1 text-sm text-slate-400">
                Abbiamo pre-popolato i tuoi dati iniziali: puoi iniziare subito a monitorare spese e ricorrenti.
              </p>

              <div className="mt-5 grid gap-3 sm:grid-cols-3">
                <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                  <p className="text-xs text-slate-400">Profilo</p>
                  <p className="mt-1 text-sm font-medium text-white">{USE_CASE_OPTIONS.find((o) => o.value === useCase)?.title}</p>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                  <p className="text-xs text-slate-400">Ricorrenti inseriti</p>
                  <p className="mt-1 text-sm font-medium text-white">{recurringNormalized.length}</p>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                  <p className="text-xs text-slate-400">Waste scanner</p>
                  <p className="mt-1 text-sm font-medium text-amber-300">€{wasteYearly.toFixed(0)}/anno</p>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <Button variant="ghost" onClick={() => setStep(3)}>
                  Indietro
                </Button>
                <Button onClick={finishOnboarding} loading={saving} leftIcon={saving ? <Loader2 size={15} /> : <Sparkles size={15} />}>
                  Entra in Dashboard
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
